/**
 * BanditContext — session cache of bandit posterior snapshots and lifecycle
 * control docs for every experiment registered for the current course.
 *
 * Mounted inside `Authenticated` (next to `ExperimentProvider`). Fetches once
 * per course and keeps the snapshot frozen for the session: assignments are
 * sticky, so mid-session posterior updates must never re-sample anyone.
 * Courses with no registered experiments cost zero reads.
 *
 * After loading, stale snapshots trigger a fire-and-forget call to the
 * `refreshbanditposterior` function — the demand-driven replacement for a
 * cron. See the @cip/bandit README for the architecture.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {doc, getDoc, getFirestore} from 'firebase/firestore';
import {
  banditControlSchema,
  banditSnapshotSchema,
  banditControlDocPath,
  banditSnapshotDocPath,
  getExperimentsForCourse,
  type BanditControl,
  type BanditSnapshot,
} from '@cip/bandit';
import {useCourseIdWithoutFallback} from 'hooks/router/useUrlParams';
import {requestBanditRefresh} from './refreshBanditPosteriorApi';
import {toMillisOrNull} from './firestoreTime';

/**
 * Snapshots older than this many milliseconds trigger a background refresh.
 * Staleness up to this bound is just batched Thompson sampling — harmless.
 */
const SNAPSHOT_STALE_MS = 30 * 60 * 1000;

/** Value provided by {@link BanditProvider}. */
export interface BanditContextValue {
  /**
   * True once snapshot/control fetches for the current course settled (also
   * true immediately for courses with no registered experiments). While
   * false, `useBanditArm` samples from the prior rather than waiting.
   */
  loaded: boolean;
  /** experimentId → parsed snapshot, or null when absent/invalid. */
  snapshots: Record<string, BanditSnapshot | null>;
  /** experimentId → parsed control doc, or null when absent (= active). */
  controls: Record<string, BanditControl | null>;
}

const DEFAULT_VALUE: BanditContextValue = {
  loaded: false,
  snapshots: {},
  controls: {},
};

const BanditContext = createContext<BanditContextValue>(DEFAULT_VALUE);

/**
 * Reads {@link BanditContext}.
 *
 * @return The current bandit cache value (defaults to not-loaded outside a
 *   provider, which makes `useBanditArm` fall back to prior sampling).
 */
export function useBanditContext(): BanditContextValue {
  return useContext(BanditContext);
}

/**
 * Provider component; see module docs.
 *
 * @param props.children - Subtree that may call `useBanditArm`.
 * @return Provider element.
 */
export function BanditProvider({children}: {children: ReactNode}) {
  const courseId = useCourseIdWithoutFallback();
  const [value, setValue] = useState<BanditContextValue>(DEFAULT_VALUE);

  useEffect(() => {
    const experiments = courseId ? getExperimentsForCourse(courseId) : [];
    if (experiments.length === 0) {
      setValue({loaded: true, snapshots: {}, controls: {}});
      return;
    }
    let cancelled = false;
    void (async () => {
      const firestore = getFirestore();
      const snapshots: Record<string, BanditSnapshot | null> = {};
      const controls: Record<string, BanditControl | null> = {};
      await Promise.all(
        experiments.map(async experiment => {
          const ref = {courseId: experiment.courseId, experimentId: experiment.experimentId};
          try {
            const [snapshotDoc, controlDoc] = await Promise.all([
              getDoc(doc(firestore, banditSnapshotDocPath(ref))),
              getDoc(doc(firestore, banditControlDocPath(ref))),
            ]);
            const parsedSnapshot = banditSnapshotSchema.safeParse(
              snapshotDoc.data(),
            );
            snapshots[experiment.experimentId] = parsedSnapshot.success
              ? parsedSnapshot.data
              : null;
            const parsedControl = banditControlSchema.safeParse(
              controlDoc.data(),
            );
            controls[experiment.experimentId] = parsedControl.success
              ? parsedControl.data
              : null;
          } catch (e) {
            console.debug('[bandit] snapshot fetch failed', experiment.experimentId, e);
            snapshots[experiment.experimentId] = null;
            controls[experiment.experimentId] = null;
          }
        }),
      );
      if (cancelled) {
        return;
      }
      setValue({loaded: true, snapshots, controls});

      // Demand-driven refresh: ask the backend to recompute stale posteriors.
      // Fire-and-forget — this session keeps sampling from the snapshot it
      // already has (batched Thompson sampling); the refresh serves the next
      // students to arrive.
      for (const experiment of experiments) {
        const control = controls[experiment.experimentId];
        if (control?.status === 'concluded') {
          continue;
        }
        const computedAtMs = toMillisOrNull(
          snapshots[experiment.experimentId]?.computedAt,
        );
        const isStale =
          computedAtMs === null ||
          Date.now() - computedAtMs > SNAPSHOT_STALE_MS;
        if (isStale) {
          requestBanditRefresh({
            courseId: experiment.courseId,
            experimentId: experiment.experimentId,
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseId]);

  return (
    <BanditContext.Provider value={value}>{children}</BanditContext.Provider>
  );
}
